import { useState, useEffect } from 'react';
import {X,HelpCircle} from 'lucide-react';
import { SlideData,Hotspot } from '../types';


interface SidebarProps {
  slides: SlideData;
  activeSlide: number;
  activeHotspot: { slideIndex: number; hotspotIndex: number } | null;
  onHotspotClick: (slideIndex: number, hotspot: Hotspot, hotspotIndex: number) => void;
  onHotspotHover: (slideIndex: number, hotspotIndex: number) => void;
  onHotspotLeave: () => void;
}

export function Sidebar({
  slides,
  activeSlide,
  activeHotspot,
  onHotspotClick,
  onHotspotHover,
  onHotspotLeave
}: SidebarProps) {

  const [showInfoBox, setShowInfoBox] = useState(true);
  const [uniqueHotspots, setUniqueHotspots] = useState({});

/*

  Referencing the "slides" SlideData, each slide contains multiple "hotspots".
  Each individual "hotspot" is identified by a "number" and "parentIndex" property.
  
  Duplicate hotspot "number"s exist within the slideData object.

  When this component is mounted, we need to loop through the slideData
  and find which "hotspots" have a duplicate "number" property.

  If a duplicate hotspot is found, we need to remove the ones except the ones where activeSlide !== parentIndex

  Finally, we will need to replace the slides object with a new filteredSlides object which will display our sidebar items.

*/
useEffect(() => {

  const hotspotNumbers = new Set();

  // Add the initial hotspots to the set from the activeSlide
  slides.map((slide) => {
    slide.hotspots.map((hotspot) => {
      if (hotspot.parentIndex === activeSlide) {
        hotspotNumbers.add(hotspot.number);
      }
    });
  });

  // filter out any duplicate hotspots from the slides object
  const filteredSlides = slides.map((slide) => {

    const filteredHotspots = slide.hotspots.filter((hotspot) => {
      if (hotspotNumbers.has(hotspot.number)) {
        return hotspot.parentIndex === activeSlide;
      } else {
        hotspotNumbers.add(hotspot.number);
        return true;
      }
    });
    return { ...slide, hotspots: filteredHotspots };
  });

  setUniqueHotspots(filteredSlides);

}, [slides, activeSlide]);


  return (
    <div className="pt-6 lg:p-6 lg:pr-0 xl:pl-8 w-full h-full lg:max-h-[calc(100vh-97px)] overflow-y-auto hide-scrollbars lg:border-l border-gray-50">
      
      {showInfoBox && (
        <div className="bg-primary-lighter border-primary-light ring-secondary ring-4 text-primary-dark border rounded-md p-4 mx-1 lg:mb-8">
          <div className="flex items-start justify-between mb-2">
            <div className="rounded-full w-8 h-8 bg-white flex items-center justify-center">
              <HelpCircle size={16} strokeWidth={2} className="text-primary-light" />
            </div>
            <button onClick={() => setShowInfoBox(false)}>
              <X size={16} strokeWidth={2} className="cursor-pointer text-primary"/>
            </button>
          </div>
          <h3 className="text-xl font-semibold mb-4">Navigate the SCC NRA Virtual Booth</h3>
          <p className="text-sm">Explore our virtual booth, a replica of what we will be showing at NRA.  Click on the VIEW carousel below to see it from various angles. Each view in the carousel highlights key points of interest.  Simply click on each number to learn more and access additional information. For a direct overview of all highlights, click on the FULL TRADESHOW FEATURES list below to see specific details.</p>
        </div>
      )}

      <h2 className="text-xl lg:text-2xl text-primary-dark font-medium mt-8 lg:mt-0 mb-2">Full Tradeshow Features</h2>
      <div>
        {Array.isArray(uniqueHotspots) && uniqueHotspots.length > 0 && uniqueHotspots.map((slide: {hotspots: Hotspot[]}, slideIndex: number) => (
          <ul key={slideIndex}>
              {slide.hotspots.map((hotspot: Hotspot, hotspotIndex: number) => (
                <li
                  key={hotspotIndex}
                  className={`cursor-pointer flex items-center py-6 border-b-2 border-gray-50 hover:border-primary transition-colors duration-500 ${
                    activeSlide === slideIndex ? '' : ''
                  } ${
                    activeHotspot?.slideIndex === slideIndex && activeHotspot?.hotspotIndex === hotspotIndex
                      ? 'border-primary'
                      : ''
                  }`}
                  onClick={() => onHotspotClick(hotspot.parentIndex, hotspot, hotspotIndex)}
                  onMouseEnter={() => onHotspotHover(slideIndex, hotspotIndex)}
                  onMouseLeave={onHotspotLeave}
                >
                  <div className="w-8 h-8 rounded-full bg-tertiary-light flex items-center justify-center text-primary-dark text-sm mr-4">
                  {hotspot.number}
                  </div>

                  <h4 className={`flex-1 text-balance font-medium ${
                    activeHotspot?.slideIndex === slideIndex && activeHotspot?.hotspotIndex === hotspotIndex
                      ? 'text-primary'
                      : 'text-primary-dark'
                  }`}>
                    {hotspot.title}
                  </h4>

                </li>
              ))}
            </ul>
        ))}
      </div>
    </div>
  );
}